/**
 * Building blocks shared by the Participants editor's rows (editable, merged,
 * automatically merged and split back), so every row lines up the same way.
 */
import React from 'react';
import { CO_SELLER_PARTICIPANT_COPY, ParticipantRole } from '../../config/participants';

// Same order as the live roster popups in the apps.
const ROLE_OPTIONS: { value: ParticipantRole; label: string }[] = [
  { value: ParticipantRole.SALESPERSON, label: 'Salesperson' },
  { value: ParticipantRole.CUSTOMER, label: 'Customer' },
];

export interface RoleToggleProps {
  value: ParticipantRole;
  onChange: (role: ParticipantRole) => void;
  disabled: boolean;
  personName: string;
}

export const RoleToggle: React.FC<RoleToggleProps> = ({ value, onChange, disabled, personName }) => (
  <div role="radiogroup" aria-label={`Role of ${personName}`} className="inline-flex rounded-md border border-hairline overflow-hidden">
    {ROLE_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        role="radio"
        aria-checked={value === option.value}
        disabled={disabled}
        onClick={() => onChange(option.value)}
        className={`px-2.5 py-1 text-xs font-medium transition-colors ${
          value === option.value ? 'bg-signature text-on-signature' : 'bg-white text-ink-secondary hover:bg-surface-subtle'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/** Fixed slot for "Same as…": kept when hidden so every row's columns line up. */
export const SAME_AS_SLOT_CLASS = 'w-40 flex-shrink-0';

export const ColorDot: React.FC<{ color: string }> = ({ color }) => (
  <span aria-hidden="true" className="inline-block w-3 h-3 mt-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
);

/** A rep who joined with their own mic: a category, so one neutral chip. */
export const OwnMicBadge: React.FC = () => (
  <span
    data-testid="own-mic-badge"
    title={CO_SELLER_PARTICIPANT_COPY.roleLocked}
    className="inline-flex flex-shrink-0 items-center px-2 py-0.5 text-xs font-medium rounded-full bg-surface-sunken text-ink-secondary"
  >
    {CO_SELLER_PARTICIPANT_COPY.ownMicBadge}
  </span>
);
